/**
 * ContractAssignmentDetailPanel
 * Panel de detalle para jobs de tipo ContractAssignment.
 * Recibe el resultado completo por prop desde JobDetailDrawer — cero re-fetch.
 * Usa DataGridPro con master-detail para equipments anidados.
 */
import {
  Box,
  Typography,
  Alert,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  DataGridPro,
  GridToolbarQuickFilter,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import {
  CheckCircleOutlined,
  HighlightOffOutlined,
  RemoveCircleOutlineOutlined,
  SwapHorizOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import type {
  AssignContractLinesResultDto,
  BookingAssignmentDetailDto,
  EquipmentAssignmentDetailDto,
} from "@/types/jobs";
import { STAT_NUMBER_SX, SECTION_LABEL_SX, MONO_SX, CHIP_GEOMETRIC_SX } from "../jobs-design-tokens";

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sublabel, color }: {
  icon: React.ReactNode; label: string; value: number; sublabel: string; color: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
        py: 1.5,
        px: 0.5,
        borderRadius: 2,
        bgcolor: `${color}0a`,
        border: "1px solid",
        borderColor: `${color}20`,
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: `0 2px 8px ${color}15` },
      }}
    >
      <Box sx={{ color, display: "flex", mb: 0.25 }}>{icon}</Box>
      <Typography sx={{ ...STAT_NUMBER_SX, color, fontSize: "24px" }}>{value ?? 0}</Typography>
      <Typography sx={{ ...SECTION_LABEL_SX, color: "text.primary", lineHeight: 1.2, textAlign: "center" }}>{label}</Typography>
      <Typography variant="caption" color="text.disabled" lineHeight={1.2} textAlign="center" sx={{ fontSize: "0.63rem" }}>{sublabel}</Typography>
    </Box>
  );
}

// ─── Equipment detail sub-table ───────────────────────────────────────────────

function EquipmentDetailGrid({ equipments }: { equipments: EquipmentAssignmentDetailDto[] }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: "rgba(8,145,178,0.03)" }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.72rem",
          "& th, & td": { px: 1, py: 0.5, textAlign: "left" },
          "& th": { color: "text.secondary", fontWeight: 600, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.06em", bgcolor: "rgba(0,0,0,0.04)", borderBottom: "1px solid", borderColor: "divider" },
          "& tr:not(:last-child) td": { borderBottom: "1px solid", borderColor: "divider" },
        }}
      >
        <thead>
          <tr><th>Código</th><th>Contenedor</th><th>Contrato</th><th>Motivo</th></tr>
        </thead>
        <tbody>
          {equipments.map((eq) => (
            <tr key={eq.BookingEquipmentId}>
              <td><Chip label={eq.EquipmentCode ?? "—"} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem", ...CHIP_GEOMETRIC_SX }} /></td>
              <td><Typography variant="caption" sx={{ ...MONO_SX, fontSize: "11px", color: "text.secondary" }}>{eq.ContainerId ?? "—"}</Typography></td>
              <td>
                {eq.NewContractLineId != null ? (
                  <Chip label={eq.ContractNumber ?? "Asignado"} size="small" color="success" sx={{ height: 18, fontSize: "0.65rem", ...CHIP_GEOMETRIC_SX }} />
                ) : (
                  <Chip label="Sin contrato" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.65rem", ...CHIP_GEOMETRIC_SX }} />
                )}
              </td>
              <td><Typography variant="caption" color={eq.NewContractLineId != null ? "text.disabled" : "warning.main"} sx={{ fontSize: "0.68rem" }}>{eq.Reason ?? "—"}</Typography></td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

// ─── Quick filter toolbar ─────────────────────────────────────────────────────

function QuickFilterToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 1, py: 0.5 }}>
      <GridToolbarQuickFilter variant="outlined" size="small" sx={{ "& .MuiOutlinedInput-root": { fontSize: "0.8rem", height: 30, borderRadius: "6px" } }} debounceMs={200} />
    </GridToolbarContainer>
  );
}

// ─── Column defs ──────────────────────────────────────────────────────────────

type BookingRow = BookingAssignmentDetailDto & { id: number };

const bookingColumns: GridColDef<BookingRow>[] = [
  {
    field: "BookingReference", headerName: "Booking", flex: 1.2,
    renderCell: ({ value, row }) => (
      <Typography variant="caption" fontWeight={700} sx={{ fontFamily: "monospace", fontSize: "12px" }}>{value ?? `#${row.BookingId}`}</Typography>
    ),
  },
  {
    field: "OperationType", headerName: "Tipo", width: 90,
    renderCell: ({ value }) => value ? <Chip label={value} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} /> : null,
  },
  {
    field: "EquipmentsAssigned", headerName: "Asignados", width: 110,
    renderCell: ({ row }) => (
      <Typography variant="caption" color={row.EquipmentsWithoutContract > 0 ? "warning.main" : "success.main"}>
        {row.EquipmentsAssigned}/{row.TotalEquipments}
      </Typography>
    ),
  },
  {
    field: "EquipmentsWithoutContract", headerName: "Sin contrato", width: 130,
    renderCell: ({ value }) =>
      (value as number) > 0 ? <Chip label={value} size="small" color="warning" sx={{ height: 18, fontSize: "0.65rem", ...CHIP_GEOMETRIC_SX }} /> : <Typography variant="caption" color="text.disabled">0</Typography>,
  },
  {
    field: "Status", headerName: "Estado", width: 110,
    renderCell: ({ row }) => {
      const allAssigned = row.EquipmentsWithoutContract === 0 && row.EquipmentsAssigned > 0;
      const partial = row.EquipmentsAssigned > 0 && !allAssigned;
      if (allAssigned) return <CheckCircleOutlined sx={{ fontSize: 16, color: "#059669" }} />;
      if (partial) return <HighlightOffOutlined sx={{ fontSize: 16, color: "#d97706" }} />;
      return <RemoveCircleOutlineOutlined sx={{ fontSize: 16, color: "#dc2626" }} />;
    },
  },
];

// ─── Panel principal ──────────────────────────────────────────────────────────

interface Props { result: AssignContractLinesResultDto; }

export function ContractAssignmentDetailPanel({ result }: Props) {
  const bookings = (result.Assignments ?? []).map((b) => ({ ...b, id: b.BookingId })) as BookingRow[];
  const hasSkipped = (result.TotalEquipmentsSkipped ?? 0) > 0;
  const allUpdated = !hasSkipped && (result.TotalEquipmentsUpdated ?? 0) > 0;

  function getDetailPanelContent({ row }: GridRowParams<BookingRow>) {
    const equipments = row.EquipmentAssignments;
    if (!equipments?.length) return null;
    return <EquipmentDetailGrid equipments={equipments} />;
  }

  function getDetailPanelHeight() { return "auto" as const; }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexShrink: 0 }}>
        <StatCard icon={<SwapHorizOutlined fontSize="small" />} label="Bookings" value={result.TotalBookingsProcessed ?? 0} sublabel="procesados" color="#0891b2" />
        <StatCard icon={<CheckCircleOutlined fontSize="small" />} label="Asignados" value={result.TotalEquipmentsUpdated ?? 0} sublabel="equipos" color="#059669" />
        <StatCard icon={<HighlightOffOutlined fontSize="small" />} label="Sin contrato" value={result.TotalEquipmentsSkipped ?? 0} sublabel="sin match" color={hasSkipped ? "#d97706" : "#6b7280"} />
        <StatCard icon={<RemoveCircleOutlineOutlined fontSize="small" />} label="Sin cambio" value={result.TotalEquipmentsUnchanged ?? 0} sublabel="ya asignados" color="#6b7280" />
      </Box>

      {/* Alertas */}
      <Box sx={{ flexShrink: 0 }}>
        {allUpdated && (
          <Alert severity="success" icon={<CheckCircleOutlined fontSize="small" />} sx={{ py: 0.5, mb: 1.5, borderRadius: "6px" }}>
            <Typography variant="caption" fontWeight={600}>Todos los equipos fueron asignados correctamente.</Typography>
          </Alert>
        )}
        {hasSkipped && (
          <Alert severity="warning" icon={<InfoOutlined fontSize="small" />} sx={{ py: 0.5, mb: 1.5, borderRadius: "6px" }}>
            <Typography variant="caption" fontWeight={600} display="block">{result.TotalEquipmentsSkipped} equipo{(result.TotalEquipmentsSkipped ?? 0) > 1 ? "s" : ""} sin contrato aplicable</Typography>
            <Typography variant="caption" color="text.secondary">Verificar tarifas vigentes para la ruta y tipo de equipo. Expandí el booking para ver el motivo exacto.</Typography>
          </Alert>
        )}
      </Box>

      {/* DataGridPro */}
      {bookings.length > 0 ? (
        <>
          <Divider sx={{ mb: 1, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Tooltip title="Expandí una fila para ver el detalle de equipos" placement="top-start">
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.75 }}>
                Detalle por booking ({bookings.length})
              </Typography>
            </Tooltip>
            <DataGridPro
              rows={bookings}
              columns={bookingColumns as GridColDef[]}
              density="compact"
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              pagination
              disableColumnMenu={false}
              slots={{ toolbar: QuickFilterToolbar }}
              sx={{
                border: 0,
                height: "100%",
                "& .MuiDataGrid-cell:focus": { outline: "none" },
                "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.015)" },
                borderRadius: "8px",
                overflow: "hidden",
              }}
              localeText={{ noRowsLabel: "Sin datos" }}
            />
          </Box>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          El detalle por booking no está disponible en el resultado de este job.
        </Typography>
      )}
    </Box>
  );
}
